import { useState } from 'react';

interface RestaurantHeroProps {
  name: string;
  logoUrl: string | null;
  coverImageUrl: string | null;
  subtitle?: string;
}

/** Palette of vibrant background colors for the letter avatar */
export const AVATAR_COLORS = [
  '#E53E3E', // red
  '#DD6B20', // orange
  '#D69E2E', // yellow
  '#38A169', // green
  '#3182CE', // blue
  '#805AD5', // purple
  '#D53F8C', // pink
  '#319795', // teal
  '#2B6CB0', // dark blue
  '#744210', // brown
];

/**
 * Derives a consistent color from the business name using a simple hash.
 */
export function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

/**
 * DoorDash-style hero section for the menu page.
 * Full-bleed cover image with a dark gradient overlay at the bottom.
 * Logo and restaurant name are displayed INSIDE the image area.
 * Action buttons float in the top-right corner with a glass-morphism style.
 */
export function RestaurantHero({ name, logoUrl, coverImageUrl, subtitle }: RestaurantHeroProps) {
  const [logoFailed, setLogoFailed] = useState(false);
  const [coverFailed, setCoverFailed] = useState(false);

  // Normalize name: trim and fallback to avoid showing '?'
  const displayName = name?.trim() || '';
  const initial = displayName[0]?.toUpperCase() ?? '?';
  const avatarColor = getAvatarColor(displayName);

  const showLogo = !!logoUrl && !logoFailed;
  const showCover = !!coverImageUrl && !coverFailed;

  return (
    <div className="relative h-64 bg-tg-secondary-bg">
      {/* Cover image / gradient placeholder */}
      <div className="absolute inset-0 overflow-hidden">
        <img
          src={showCover ? coverImageUrl! : '/default-cover.svg'}
          alt="Portada del negocio"
          className="w-full h-full object-cover"
          onError={() => {
            if (showCover) setCoverFailed(true);
          }}
        />
      </div>

      {/*
       * Dark gradient overlay — two layers for a rich, warm fade effect.
       * Bottom 60% gets a strong brown-tinted black for text readability.
       * Full image gets a subtle dark wash so buttons are always legible.
       */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-[#1a1008]/60 to-transparent" />

      {/*
       * Logo + name — positioned at the bottom-left of the hero, inside the image.
       * The dark gradient ensures great contrast for the white text.
       */}
      <div className="absolute inset-x-0 bottom-0 z-10 flex items-center gap-3 px-4 pb-4">
        {/* Logo circle */}
        <div className="w-16 h-16 flex-shrink-0 rounded-full overflow-hidden shadow-lg">
          {showLogo ? (
            <img
              src={logoUrl!}
              alt={`Logo de ${displayName}`}
              className="w-full h-full object-cover"
              onError={() => setLogoFailed(true)}
            />
          ) : (
            <div
              className="w-full h-full flex items-center justify-center text-2xl font-bold text-white select-none"
              style={{ backgroundColor: avatarColor }}
            >
              {initial}
            </div>
          )}
        </div>

        {/* Name + subtitle */}
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-white leading-tight truncate drop-shadow-md">
            {displayName}
          </h1>
          {subtitle && (
            <p className="mt-0.5 text-xs text-white/75 truncate drop-shadow-sm">
              {subtitle}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

